// Thin Capacitor/native sensor boundary for NDG acceptance work.
// This bridge does not validate hardware, calibrate axes or create teaching evidence.
// Any capture produced here still enters the server as unverified + teaching-ineligible.

const PLUGIN_NAME = "NDGSensors";
const ALLOWED_SENSORS = new Set(["acceleration", "rotation_rate", "orientation"]);
const MAX_DURATION_MS = 20000;
const MAX_SAMPLES = 400;
const MIN_SAMPLE_INTERVAL_MS = 50;

function runtimeHost(host) {
  if (host) return host;
  if (typeof globalThis !== "undefined") return globalThis;
  return null;
}

function finite(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function nonNegative(value) {
  const number = finite(value);
  return number !== null && number >= 0 ? number : null;
}

function vector(value, axes) {
  return Object.fromEntries(axes.map((axis) => [axis, finite(value?.[axis])]));
}

export function getNativeSensorDiagnosticsPlugin(host) {
  const root = runtimeHost(host);
  if (!root) return null;

  if (root.__NDG_NATIVE_SENSORS__) return root.__NDG_NATIVE_SENSORS__;

  const capacitor = root.Capacitor;
  if (!capacitor) return null;
  if (
    typeof capacitor.isNativePlatform === "function"
    && !capacitor.isNativePlatform()
  ) return null;
  if (
    typeof capacitor.isPluginAvailable === "function"
    && !capacitor.isPluginAvailable(PLUGIN_NAME)
  ) return null;
  return capacitor.Plugins?.[PLUGIN_NAME] || null;
}

export function nativeSensorDiagnosticsAvailable(host) {
  const plugin = getNativeSensorDiagnosticsPlugin(host);
  return Boolean(
    plugin
    && typeof plugin.start === "function"
    && typeof plugin.stop === "function"
    && typeof plugin.addListener === "function"
  );
}

export function normalizeNativeSensorSample(sensor, event, sequence) {
  if (!ALLOWED_SENSORS.has(sensor)) {
    throw new Error("Unsupported native sensor.");
  }
  const elapsed = nonNegative(event?.elapsedMs);
  if (elapsed === null) throw new Error("Native sensor sample is missing elapsed time.");

  const sample = {
    sequence,
    elapsed_ms: elapsed,
    device_epoch_ms: nonNegative(event?.deviceEpochMs),
    event_timestamp_ms: nonNegative(event?.eventTimestampMs),
    screen_angle: finite(event?.screenAngle),
  };
  if (sample.device_epoch_ms === null) {
    throw new Error("Native sensor sample is missing device time.");
  }

  if (sensor === "acceleration") {
    sample.interval_ms = nonNegative(event?.intervalMs);
    sample.acceleration = vector(event?.acceleration, ["x", "y", "z"]);
    sample.acceleration_including_gravity = vector(
      event?.accelerationIncludingGravity,
      ["x", "y", "z"],
    );
  } else if (sensor === "rotation_rate") {
    sample.interval_ms = nonNegative(event?.intervalMs);
    sample.rotation_rate = vector(event?.rotationRate, ["alpha", "beta", "gamma"]);
  } else {
    sample.orientation = vector(event?.orientation, ["alpha", "beta", "gamma"]);
    sample.absolute = typeof event?.absolute === "boolean" ? event.absolute : null;
  }
  return sample;
}

async function removeHandle(handle) {
  try { await handle?.remove?.(); } catch {}
}

export function createNativeSensorDiagnostic({
  sensor,
  movement,
  mount,
  onSample,
  onFinish,
  host,
}) {
  const root = runtimeHost(host);
  const plugin = getNativeSensorDiagnosticsPlugin(root);
  if (!plugin) throw new Error("Native sensor diagnostics are unavailable.");
  if (
    typeof plugin.start !== "function"
    || typeof plugin.stop !== "function"
    || typeof plugin.addListener !== "function"
  ) {
    throw new Error("Native sensor diagnostics plugin is incomplete.");
  }
  if (!ALLOWED_SENSORS.has(sensor)) throw new Error("Choose a supported sensor.");

  let started = false;
  let active = false;
  let finished = false;
  let cancelled = false;
  let cancellationReason = "user_stopped";
  let capture = null;
  let sampleHandle = null;
  let stoppedHandle = null;
  let lastElapsed = -Infinity;

  async function removeListeners() {
    await removeHandle(sampleHandle);
    await removeHandle(stoppedHandle);
    sampleHandle = null;
    stoppedHandle = null;
  }

  async function finish(reason = "user_stopped", durationMs = null) {
    if (finished || !capture) return;
    finished = true;
    active = false;
    await removeHandle(sampleHandle);
    await removeHandle(stoppedHandle);
    sampleHandle = null;
    stoppedHandle = null;
    const finalDuration = nonNegative(durationMs);
    capture.duration_ms = Math.min(
      60000,
      finalDuration ?? Math.max(0, capture.samples.at(-1)?.elapsed_ms || 0),
    );
    capture.stop_reason = reason;
    onFinish?.(capture);
  }

  async function start() {
    if (started) throw new Error("Start a new sensor check.");
    started = true;
    if (cancelled) return false;

    const captureId = root?.crypto?.randomUUID?.();
    if (!captureId) throw new Error("This device cannot create a sensor capture ID.");

    sampleHandle = await plugin.addListener?.("sample", async (event) => {
      if (!active || finished || event?.sensor !== sensor) return;
      const elapsed = nonNegative(event?.elapsedMs);
      if (elapsed === null || elapsed < lastElapsed + MIN_SAMPLE_INTERVAL_MS) return;
      if (elapsed > MAX_DURATION_MS) {
        await stop("completed");
        return;
      }
      const sample = normalizeNativeSensorSample(sensor, event, capture.samples.length);
      lastElapsed = sample.elapsed_ms;
      capture.samples.push(sample);
      onSample?.(sample, capture.samples.length);
      if (capture.samples.length >= MAX_SAMPLES) await stop("sample_limit");
    });
    if (cancelled) { await removeListeners(); return false; }

    stoppedHandle = await plugin.addListener?.("stopped", async (event) => {
      if (!active || finished || event?.sensor !== sensor) return;
      await finish(
        event?.reason || "completed",
        event?.durationMs,
      );
    });
    if (cancelled) { await removeListeners(); return false; }

    const result = await plugin.start?.({
      sensor,
      maxDurationMs: MAX_DURATION_MS,
      maxSamples: MAX_SAMPLES,
      minSampleIntervalMs: MIN_SAMPLE_INTERVAL_MS,
    });
    // stop() can run while native permission/startup is still pending.
    // Dispose a late grant without activating or persisting the old capture.
    if (cancelled) {
      try {
        if (result?.permission === "granted") {
          await plugin.stop({ sensor, reason: cancellationReason });
        }
      } finally {
        await removeListeners();
      }
      return false;
    }
    if (!result || result.permission !== "granted") {
      await removeHandle(sampleHandle);
      await removeHandle(stoppedHandle);
      sampleHandle = null;
      stoppedHandle = null;
      throw new Error("Sensor permission was not granted. No readings were taken.");
    }

    const startedEpoch = nonNegative(result.startedDeviceEpochMs);
    if (startedEpoch === null) {
      await removeHandle(sampleHandle);
      await removeHandle(stoppedHandle);
      throw new Error("Native sensor clock is unavailable.");
    }

    capture = {
      capture_id: captureId,
      capture_transport: "native_device_events",
      sensor,
      movement,
      mount,
      permission: "granted",
      started_device_epoch_ms: startedEpoch,
      time_origin_ms: nonNegative(result.timeOriginMs),
      duration_ms: 0,
      stop_reason: "completed",
      samples: [],
    };
    active = true;
    return true;
  }

  async function stop(reason = "user_stopped") {
    if (!active) { cancelled = true; cancellationReason = reason; }
    if (!active || finished) return;
    try {
      const result = await plugin.stop?.({ sensor, reason });
      await finish(reason, result?.durationMs);
    } catch {
      await finish(reason);
    }
  }

  return { start, stop, mode: "native" };
}

export const NATIVE_SENSOR_DIAGNOSTICS_LIMITS = Object.freeze({
  maxDurationMs: MAX_DURATION_MS,
  maxSamples: MAX_SAMPLES,
  minSampleIntervalMs: MIN_SAMPLE_INTERVAL_MS,
});

